import { c as _c } from "react/compiler-runtime";
import { BrandCardHeader } from "@repo/ui/components/brand/BrandCardHeader";
import { Button } from "@repo/ui/components/ui/button";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import { StatusMessage } from "@repo/ui/components/ui/status-message";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { authClient } from "@/clients/authClient";
import { BaseLayout } from "@/lib/components/layouts/BaseLayout";
import { emailVerifiedSearchSchema } from "@/lib/schemas/searchParams.schema";
import { safeRedirectPath } from "@/lib/utils/safeRedirect";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const Route = createFileRoute("/_public/e-post-verifierad")({
  component: EmailVerifiedPage,
  validateSearch: emailVerifiedSearchSchema
});

/**
 * Where Better Auth sends the user after consuming a verification token.
 *
 * Not under `_public/_auth` on purpose — that layout bounces signed-in users to
 * "/", and `autoSignInAfterVerification` means we arrive already signed in.
 */
function EmailVerifiedPage() {
  const $ = _c(16);
  const {
    next,
    error
  } = Route.useSearch();
  const navigate = useNavigate();
  const {
    data: session,
    isPending
  } = authClient.useSession();
  let t0;
  if ($[0] !== next) {
    t0 = safeRedirectPath(next, window.location.origin);
    $[0] = next;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const target = t0;
  let t1;
  if ($[2] !== target) {
    t1 = () => {
      window.location.assign(target);
    };
    $[2] = target;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const goNext = t1;
  const t2 = error ? "L\xE4nken fungerade inte" : "E-postadressen \xE4r klar";
  const t3 = error ? "Verifieringsl\xE4nken \xE4r ogiltig eller har g\xE5tt ut" : "Tack, ditt konto \xE4r nu aktiverat";
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = /*#__PURE__*/_jsx(BrandCardHeader, {
      title: t2,
      subtitle: t3
    });
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== error || $[8] !== goNext || $[9] !== isPending || $[10] !== navigate || $[11] !== session) {
    t5 = /*#__PURE__*/_jsx(CardContent, {
      className: "space-y-6 p-6",
      children: error ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(StatusMessage, {
          variant: "error",
          message: "L\xE4nken har redan anv\xE4nts eller g\xE5tt ut. Logga in s\xE5 skickar vi en ny verifieringsl\xE4nk till din e-post."
        }), /*#__PURE__*/_jsx(Button, {
          className: "w-full",
          onClick: () => navigate({
            to: "/logga-in"
          }),
          children: "Till inloggningen"
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs("div", {
          className: "space-y-4 text-center",
          children: [/*#__PURE__*/_jsx("div", {
            className: "flex justify-center",
            children: /*#__PURE__*/_jsx("div", {
              className: "rounded-full bg-green-50 p-4",
              children: /*#__PURE__*/_jsx(CheckCircle2, {
                className: "h-8 w-8 text-green-600",
                "aria-hidden": "true"
              })
            })
          }), /*#__PURE__*/_jsx("p", {
            className: "text-muted-foreground text-sm",
            children: isPending ? "Loggar in dig\u2026" : session ? `Du är inloggad som ${session.user.email}.` : "Din e-postadress \xE4r verifierad. Logga in f\xF6r att forts\xE4tta."
          })]
        }), isPending ? null : session ? /*#__PURE__*/_jsx(Button, {
          className: "w-full",
          onClick: goNext,
          children: "Forts\xE4tt"
        }) : /*#__PURE__*/_jsx(Button, {
          className: "w-full",
          onClick: () => navigate({
            to: "/logga-in"
          }),
          children: "Logga in"
        })]
      })
    });
    $[7] = error;
    $[8] = goNext;
    $[9] = isPending;
    $[10] = navigate;
    $[11] = session;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = /*#__PURE__*/_jsx(BaseLayout, {
      headerMode: "minimal",
      showFooter: false,
      children: /*#__PURE__*/_jsx("div", {
        className: "flex min-h-screen",
        children: /*#__PURE__*/_jsxs(Card, {
          className: "bg-card m-auto w-screen rounded-none shadow-none sm:max-w-md sm:rounded-lg sm:shadow-lg sm:shadow-zinc-500/10",
          children: [t4, t5]
        })
      })
    });
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}